// TODO: AI generated view scoring system, to be reviewed and adjusted

import { StatsMap } from '@/workers/viewAnalysis.worker';
import { HorizonContext, HORIZON_CONTEXT_SCORES } from '@/types/horizonContext';

export type ViewData = Record<string, StatsMap>;

export interface ViewScoreBreakdown {
	openScore: number; // Score for open sky views (0-30)
	beyondHorizonBonus: number; // Bonus from beyond-horizon assumptions (0 to +12)
	beyondHorizonEnabled: boolean; // Whether beyond-horizon assumptions were enabled
	vegetationScore: number; // Score for vegetation views (0-20)
	terrainScore: number; // Score for terrain views (0-10)
	waterScore: number; // Score for water views (0-15)
	distanceScore: number; // Score based on average view distances (0-8)
	skyViewFactor: number; // Sky view factor score (0-10)
	viewDiversity: number; // View diversity score (0-7)
	horizontalOpenness: number; // Score for unobstructed horizontal views (0-10)
	buildingPenalty: number; // Penalty for building obstructions (0 to -15)
	roadPenalty: number; // Penalty for road proximity (0 to -10)
	landmarkBonus: number; // Bonus for landmark views (0 to +10)
	parkBonus: number; // Bonus for painted park/greenery/water features (0 to +8)
	totalScore: number; // Final score (0-100)
	breakdown: {
		open: number;
		vegetation: number;
		water: number;
		building: number;
		road: number;
		terrain: number;
		unknown: number;
		landmark: number; // Percentage of rays hitting landmarks
		paintedFeature: number; // Percentage of rays hitting painted features
	};
	svfValue: number; // Raw SVF value (0-1)
	diversityIndex: number; // Raw diversity index (0-1)
	horizontalOpennessValue: number; // Raw horizontal openness (0-1)
}

/**
 * Calculate Sky View Factor (SVF) from the view analysis data
 * SVF measures the ratio of visible sky hemisphere - important for natural light and psychological well-being
 * Higher SVF = more open sky = better for mental health and natural lighting
 *
 * In our Z-up coordinate system, we focus on upward-facing rays
 */
const calculateSkyViewFactor = (statsMap: Record<string, StatsMap>): number => {
	// SVF is primarily determined by open sky views, weighted by upward rays
	const totalRays = Object.values(statsMap).reduce((sum, stats) => sum + stats.count, 0);

	if (totalRays === 0) return 0;

	// Open rays contribute most to SVF, especially upward rays
	const openStats = statsMap.open;
	if (!openStats) return 0;

	// Weight upward rays more heavily for SVF (they represent sky visibility)
	const upwardWeight = 1.5;
	const horizontalWeight = 0.8;
	const downwardWeight = 0.3;

	const weightedOpenCount =
		(openStats.upwardCount || 0) * upwardWeight +
		(openStats.horizontalCount || 0) * horizontalWeight +
		(openStats.downwardCount || 0) * downwardWeight;

	// Distant objects (>200m) also contribute slightly to feeling of openness
	let distantObjectCount = 0;
	['vegetation', 'water', 'terrain', 'building'].forEach((cat) => {
		if (statsMap[cat] && statsMap[cat].averageDistance > 200) {
			distantObjectCount += statsMap[cat].count * 0.2; // 20% contribution for distant objects
		}
	});

	const effectiveOpenCount = weightedOpenCount + distantObjectCount;
	const maxPossibleWeighted = totalRays * upwardWeight; // Maximum if all rays were upward open
	const svf = Math.min(effectiveOpenCount / maxPossibleWeighted, 1.0);

	return svf;
};

/**
 * Calculate Horizontal Openness - critical for quality of living
 * Horizontal views (±10° from eye level) are most important for human perception
 * Unobstructed horizontal views improve sense of space and reduce claustrophobia
 */
const calculateHorizontalOpenness = (statsMap: Record<string, StatsMap>): number => {
	let totalHorizontalRays = 0;
	let openHorizontalRays = 0;
	let positiveHorizontalRays = 0; // Open + distant natural elements

	Object.entries(statsMap).forEach(([category, stats]) => {
		const horizontalCount = stats.horizontalCount || 0;
		totalHorizontalRays += horizontalCount;

		if (category === 'open') {
			openHorizontalRays += horizontalCount;
			positiveHorizontalRays += horizontalCount;
		} else if (
			(category === 'vegetation' || category === 'water' || category === 'terrain') &&
			stats.averageDistance > 100
		) {
			// Distant natural elements at horizontal level are positive
			positiveHorizontalRays += horizontalCount * 0.6;
		}
	});

	if (totalHorizontalRays === 0) return 0;

	// Weight pure openness heavily, but also reward distant natural views
	const opennessRatio = openHorizontalRays / totalHorizontalRays;
	const positiveRatio = positiveHorizontalRays / totalHorizontalRays;

	return Math.min(opennessRatio * 0.7 + positiveRatio * 0.3, 1.0);
};

/**
 * Calculate View Diversity using Shannon Diversity Index
 * Measures variety in what's visible - monotonous views are psychologically less stimulating
 * Higher diversity = more varied and interesting views
 */
const calculateViewDiversity = (statsMap: Record<string, StatsMap>): number => {
	const totalRays = Object.values(statsMap).reduce((sum, stats) => sum + stats.count, 0);

	if (totalRays === 0) return 0;

	// Calculate Shannon Diversity Index: -Σ(pi * ln(pi))
	// where pi is the proportion of category i
	let shannonIndex = 0;
	const categories = ['open', 'vegetation', 'water', 'building', 'road', 'terrain'];

	categories.forEach((cat) => {
		if (statsMap[cat] && statsMap[cat].count > 0) {
			const proportion = statsMap[cat].count / totalRays;
			shannonIndex -= proportion * Math.log(proportion);
		}
	});

	// Normalize to 0-1 range
	// Maximum possible Shannon index for 6 categories is ln(6) ≈ 1.79
	const maxShannon = Math.log(categories.length);
	const normalizedDiversity = shannonIndex / maxShannon;

	return normalizedDiversity;
};

/**
 * Calculate view quality score based on what the view analysis detects
 *
 * Scoring criteria:
 * - Open sky views: 0-25 points (more open = better, adjusted by horizon contexts when enabled)
 * - Vegetation views: 0-18 points (greenery is desirable)
 * - Terrain views: 0-8 points (natural ground, lower value than vegetation)
 * - Water views: 0-12 points (water features are premium)
 * - View distance: 0-7 points (longer unobstructed views = better)
 * - Sky View Factor: 0-10 points (openness overhead for light and well-being)
 * - View Diversity: 0-7 points (variety prevents monotony)
 * - Horizontal Openness: 0-10 points (unobstructed eye-level views are critical)
 * - Building penalty: 0 to -15 points (especially for close buildings at eye level)
 * - Road penalty: 0 to -10 points (proximity penalty for noise and pollution)
 *
 * @param statsMap - Statistics map from view analysis
 * @param horizonContexts - The horizon contexts (ocean, mountains, etc.) affecting open view scoring (when enabled)
 * @param isBeyondHorizonEnabled - Whether to apply horizon context multipliers
 */
export const calculateViewScore = (
	statsMap: Record<string, StatsMap>,
	horizonContexts: HorizonContext[] = [],
	isBeyondHorizonEnabled: boolean = false
): ViewScoreBreakdown => {
	// Calculate total rays
	let totalRays = 0;
	Object.values(statsMap).forEach((stats) => {
		totalRays += stats.count;
	});

	if (totalRays === 0) {
		return {
			openScore: 0,
			beyondHorizonBonus: 0,
			beyondHorizonEnabled: isBeyondHorizonEnabled,
			vegetationScore: 0,
			terrainScore: 0,
			waterScore: 0,
			distanceScore: 0,
			skyViewFactor: 0,
			viewDiversity: 0,
			horizontalOpenness: 0,
			buildingPenalty: 0,
			roadPenalty: 0,
			landmarkBonus: 0,
			parkBonus: 0,
			totalScore: 0,
			breakdown: {
				open: 0,
				vegetation: 0,
				water: 0,
				building: 0,
				road: 0,
				terrain: 0,
				unknown: 0,
				landmark: 0,
				paintedFeature: 0,
			},
			svfValue: 0,
			diversityIndex: 0,
			horizontalOpennessValue: 0,
		};
	}

	// Calculate percentages
	const breakdown = {
		open: ((statsMap.open?.count || 0) / totalRays) * 100,
		vegetation: ((statsMap.vegetation?.count || 0) / totalRays) * 100,
		water: ((statsMap.water?.count || 0) / totalRays) * 100,
		building: ((statsMap.building?.count || 0) / totalRays) * 100,
		road: ((statsMap.road?.count || 0) / totalRays) * 100,
		terrain: ((statsMap.terrain?.count || 0) / totalRays) * 100,
		unknown: ((statsMap.unknown?.count || 0) / totalRays) * 100,
		landmark: ((statsMap.landmark?.count || 0) / totalRays) * 100,
		paintedFeature: ((statsMap.paintedFeature?.count || 0) / totalRays) * 100,
	};

	// Calculate Sky View Factor (0-1) - weighted by upward rays
	const svfValue = calculateSkyViewFactor(statsMap);
	const skyViewFactor = svfValue * 10; // Convert to 0-10 score

	// Calculate Horizontal Openness (0-1) - critical for quality of living
	const horizontalOpennessValue = calculateHorizontalOpenness(statsMap);
	const horizontalOpenness = horizontalOpennessValue * 10; // Convert to 0-10 score

	// Calculate View Diversity (0-1)
	const diversityIndex = calculateViewDiversity(statsMap);
	const viewDiversity = diversityIndex * 7; // Convert to 0-7 score

	// Calculate open sky score (0-25 points)
	// More open views = better score, optimal at 50-60%
	// Apply horizon context bonus (ocean views more valuable than urban, etc.)
	const openPercentage = breakdown.open;
	const baseOpenScore = Math.min((openPercentage / 60) * 25, 25);

	const openScore = baseOpenScore;
	let beyondHorizonBonus = 0;

	// Apply horizon context bonus only if enabled and contexts are selected
	if (isBeyondHorizonEnabled && horizonContexts.length > 0) {
		// Use the best (highest) context score as the primary bonus
		const maxContextScore = Math.max(
			...horizonContexts.map((context) => HORIZON_CONTEXT_SCORES[context])
		);

		// Convert context score to bonus points (0-8 points scale)
		// Score 4 (No_Dominant_Horizon) = 0 bonus, Score 7 (Ocean) = 8 bonus points
		const primaryBonus = Math.max(0, (maxContextScore - 4) / (7 - 4)) * 8;

		// Add additional bonus for view diversity: 1.5 points for each additional context
		// Having multiple good views (ocean + lake + mountains) is better than just one
		const diversityBonus = horizonContexts.length > 1 ? (horizonContexts.length - 1) * 1.5 : 0;

		// Cap the total bonus at 12 points
		beyondHorizonBonus = Math.min(primaryBonus + diversityBonus, 12);
	}

	// Calculate vegetation score (0-18 points)
	// Optimal around 20-30% vegetation views
	const vegPercentage = breakdown.vegetation;
	let vegetationScore = 0;
	if (vegPercentage <= 30) {
		vegetationScore = (vegPercentage / 30) * 18;
	} else {
		// Penalize if vegetation blocks too much (over 30%)
		vegetationScore = Math.max(0, 18 - ((vegPercentage - 30) / 70) * 18);
	}

	// Calculate terrain score (0-8 points)
	// Similar to vegetation but lower value, optimal around 10-20%
	const terrainPercentage = breakdown.terrain;
	let terrainScore = 0;
	if (terrainPercentage <= 20) {
		terrainScore = (terrainPercentage / 20) * 8;
	} else {
		// Penalize if terrain blocks too much (over 20%)
		terrainScore = Math.max(0, 8 - ((terrainPercentage - 20) / 80) * 8);
	}

	// Calculate water score (0-12 points)
	// Water views are premium, but rare
	const waterPercentage = breakdown.water;
	const waterScore = Math.min((waterPercentage / 10) * 12, 12);

	// Calculate distance score (0-7 points)
	// Longer average view distances are better (up to 500m optimal)
	const positiveCategories = ['open', 'vegetation', 'water', 'terrain'];
	let totalDistance = 0;
	let totalCount = 0;

	positiveCategories.forEach((cat) => {
		if (statsMap[cat]) {
			totalDistance += statsMap[cat].averageDistance * statsMap[cat].count;
			totalCount += statsMap[cat].count;
		}
	});

	const avgDistance = totalCount > 0 ? totalDistance / totalCount : 0;
	const distanceScore = Math.min((avgDistance / 500) * 7, 7);

	// Calculate building penalty (0 to -15 points)
	// Buildings block views, especially at horizontal level and when close
	let buildingPenalty = 0;
	if (statsMap.building) {
		const buildingStats = statsMap.building;
		const buildingPercentage = breakdown.building;
		const avgBuildingDistance = buildingStats.averageDistance;

		// Base penalty from building percentage (more buildings = worse)
		const percentagePenalty = (buildingPercentage / 100) * -6;

		// Angular penalty - horizontal obstruction is worst
		const totalBuildingRays = buildingStats.count;
		const horizontalRatio = (buildingStats.horizontalCount || 0) / totalBuildingRays;
		const downwardRatio = (buildingStats.downwardCount || 0) / totalBuildingRays;

		// Horizontal obstruction penalty (worst)
		const angularPenalty = horizontalRatio * -5 + downwardRatio * -1.5;

		// Distance penalty for close buildings
		let distancePenalty = 0;
		if (avgBuildingDistance < 50) {
			// Very close buildings (<50m) - severe penalty, especially horizontal
			distancePenalty = -4 * (1 - avgBuildingDistance / 50) * (1 + horizontalRatio);
		} else if (avgBuildingDistance < 100) {
			// Close buildings (50-100m) - moderate penalty
			distancePenalty = -2 * (1 - (avgBuildingDistance - 50) / 50);
		}

		buildingPenalty = percentagePenalty + angularPenalty + distancePenalty;
		buildingPenalty = Math.max(buildingPenalty, -15); // Cap at -15
	}

	// Calculate road penalty (0 to -10 points)
	// Roads bring noise and air pollution, especially when close
	let roadPenalty = 0;
	if (statsMap.road) {
		const roadPercentage = breakdown.road;
		const avgRoadDistance = statsMap.road.averageDistance;

		// Base penalty from road visibility (more roads = worse)
		const percentagePenalty = (roadPercentage / 100) * -4;

		// Heavy penalty for close roads due to noise and exhaust gases
		let distancePenalty = 0;
		if (avgRoadDistance < 30) {
			// Very close road (under 30m) - severe penalty
			distancePenalty = -6 * (1 - avgRoadDistance / 30);
		} else if (avgRoadDistance < 75) {
			// Close road (30-75m) - moderate penalty
			distancePenalty = -3 * (1 - (avgRoadDistance - 30) / 45);
		} else if (avgRoadDistance < 150) {
			// Nearby road (75-150m) - light penalty
			distancePenalty = -1 * (1 - (avgRoadDistance - 75) / 75);
		}

		roadPenalty = percentagePenalty + distancePenalty;
		roadPenalty = Math.max(roadPenalty, -10); // Cap at -10
	}

	// Calculate landmark bonus (0 to +10 points)
	// Views of landmark buildings are valuable for orientation and aesthetic appeal
	let landmarkBonus = 0;
	if (statsMap.landmark) {
		const landmarkPercentage = breakdown.landmark;
		const avgLandmarkDistance = statsMap.landmark.averageDistance;
		const horizontalCount = statsMap.landmark.horizontalCount || 0;
		const totalLandmarkRays = statsMap.landmark.count;
		
		// Base bonus from landmark visibility
		const percentageBonus = Math.min((landmarkPercentage / 15) * 6, 6); // Up to 6 points
		
		// Distance bonus - landmarks better visible from medium distance (50-200m)
		let distanceBonus = 0;
		if (avgLandmarkDistance >= 50 && avgLandmarkDistance <= 200) {
			distanceBonus = 2; // Optimal viewing distance
		} else if (avgLandmarkDistance < 50) {
			distanceBonus = 1.5 * (avgLandmarkDistance / 50); // Too close
		} else if (avgLandmarkDistance > 200) {
			distanceBonus = 2 * Math.max(0, 1 - (avgLandmarkDistance - 200) / 300); // Too far
		}
		
		// Horizontal viewing bonus - landmarks best seen at eye level
		const horizontalRatio = horizontalCount / totalLandmarkRays;
		const angularBonus = horizontalRatio * 2; // Up to 2 points
		
		landmarkBonus = percentageBonus + distanceBonus + angularBonus;
		landmarkBonus = Math.min(landmarkBonus, 10); // Cap at +10
	}

	// Calculate painted feature bonus (0 to +8 points)
	// User-painted greenery, water, and parks improve view quality
	let parkBonus = 0;
	if (statsMap.paintedFeature) {
		const featurePercentage = breakdown.paintedFeature;
		const avgFeatureDistance = statsMap.paintedFeature.averageDistance;
		
		// Base bonus from painted feature visibility
		const percentageBonus = Math.min((featurePercentage / 20) * 5, 5); // Up to 5 points
		
		// Distance bonus - natural features better when visible (50-300m)
		let distanceBonus = 0;
		if (avgFeatureDistance >= 50 && avgFeatureDistance <= 300) {
			distanceBonus = 3; // Optimal viewing distance
		} else if (avgFeatureDistance < 50) {
			distanceBonus = 2.5 * (avgFeatureDistance / 50);
		} else if (avgFeatureDistance > 300) {
			distanceBonus = 3 * Math.max(0, 1 - (avgFeatureDistance - 300) / 200);
		}
		
		parkBonus = percentageBonus + distanceBonus;
		parkBonus = Math.min(parkBonus, 8); // Cap at +8
	}

	// Calculate total score
	const rawScore =
		openScore +
		beyondHorizonBonus +
		vegetationScore +
		terrainScore +
		waterScore +
		distanceScore +
		skyViewFactor +
		viewDiversity +
		horizontalOpenness +
		buildingPenalty +
		roadPenalty +
		landmarkBonus +
		parkBonus;
	const totalScore = Math.round(rawScore);

	return {
		openScore: Math.round(openScore * 10) / 10,
		beyondHorizonBonus: Math.round(beyondHorizonBonus * 10) / 10,
		beyondHorizonEnabled: isBeyondHorizonEnabled,
		vegetationScore: Math.round(vegetationScore * 10) / 10,
		terrainScore: Math.round(terrainScore * 10) / 10,
		waterScore: Math.round(waterScore * 10) / 10,
		distanceScore: Math.round(distanceScore * 10) / 10,
		skyViewFactor: Math.round(skyViewFactor * 10) / 10,
		viewDiversity: Math.round(viewDiversity * 10) / 10,
		horizontalOpenness: Math.round(horizontalOpenness * 10) / 10,
		buildingPenalty: Math.round(buildingPenalty * 10) / 10,
		roadPenalty: Math.round(roadPenalty * 10) / 10,
		landmarkBonus: Math.round(landmarkBonus * 10) / 10,
		parkBonus: Math.round(parkBonus * 10) / 10,
		totalScore: Math.max(0, Math.min(100, totalScore)) / 10,
		breakdown,
		svfValue: Math.round(svfValue * 1000) / 1000,
		diversityIndex: Math.round(diversityIndex * 1000) / 1000,
		horizontalOpennessValue: Math.round(horizontalOpennessValue * 1000) / 1000,
	};
};
